import React, { useRef, useEffect, useState, useCallback } from "react";
import { promptDialog } from "../notify";
import {
  Bold, Italic, Underline, Strikethrough, List, ListOrdered, Indent, Outdent,
  Link2, Eraser, Palette, Highlighter, Undo2, Redo2, Subscript, Superscript,
  AlignLeft, AlignCenter, AlignRight, AlignJustify,
} from "lucide-react";
import { K } from "../theme";

// Lightweight WYSIWYG editor for email/template bodies — contentEditable +
// document.execCommand. Still functional in every browser this app targets
// despite execCommand's "deprecated" status; a rich-text component pulled
// in as a dependency (Quill/TipTap/...) would be a lot of extra weight for
// what's really a dozen formatting buttons.
//
// Merge fields (e.g. {{first_name}}) are inserted as plain text at the
// caret — clicking a chip must NOT steal focus from the editable div first
// (that would lose the caret position), so every toolbar/chip button uses
// onMouseDown={preventDefault} rather than relying on onClick alone.
//
// Value is HTML. The parent owns `value`; this component only pushes
// changes up on input/blur — it does NOT re-sync `value` back into the DOM
// on every keystroke (that fights the browser's own caret handling and
// makes text jump backwards while typing). It re-syncs only when `value`
// changes for a reason other than this editor's own onChange (e.g.
// switching which template is being edited).

const FONT_SIZES = [
  { v: "2", l: "Small" },
  { v: "3", l: "Normal" },
  { v: "5", l: "Large" },
  { v: "7", l: "Huge" },
];

const FONT_FAMILIES = ["Arial", "Helvetica", "Georgia", "Times New Roman", "Verdana", "Courier New"];
const BLOCK_STYLES = [
  { v: "p", l: "Paragraph" },
  { v: "h1", l: "Heading 1" },
  { v: "h2", l: "Heading 2" },
  { v: "h3", l: "Heading 3" },
  { v: "blockquote", l: "Quote" },
];

const COLORS = ["#111827", "#DC2626", "#D97706", "#16A34A", "#2563EB", "#7C3AED", "#DB2777"];
const HIGHLIGHTS = ["transparent", "#FEF08A", "#BBF7D0", "#BFDBFE", "#FBCFE8", "#FED7AA"];

const ToolBtn = ({ icon: Ic, title, onClick, active }) => (
  <button
    type="button"
    title={title}
    onMouseDown={(e) => e.preventDefault()}
    onClick={onClick}
    className={`p-[6px] rounded-[6px] border-0 cursor-pointer ${active ? "bg-pg text-p" : "bg-transparent text-t2 hover:bg-bg2 hover:text-t0"}`}
  >
    <Ic size={13} />
  </button>
);

const SwatchPicker = ({ colors, onPick, icon: Ic, title }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc, true);
    return () => document.removeEventListener("mousedown", onDoc, true);
  }, [open]);
  return (
    <div ref={ref} className="relative">
      <ToolBtn icon={Ic} title={title} onClick={() => setOpen((o) => !o)} />
      {open && (
        <div className="absolute z-10 top-full left-0 mt-1 p-[6px] bg-bg1 border border-b1 rounded-[8px] shadow-lg flex gap-[4px]" onMouseDown={(e) => e.preventDefault()}>
          {colors.map((c) => (
            <button
              key={c}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => { onPick(c); setOpen(false); }}
              title={c}
              className="w-[16px] h-[16px] rounded-[4px] border border-b1 cursor-pointer"
              style={{ background: c === "transparent" ? "repeating-conic-gradient(#ccc 0% 25%, #fff 0% 50%) 50%/8px 8px" : c }}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// True "is this editor visually empty" check — NOT `:empty` (a CSS
// pseudo-class that fails the moment Chrome leaves a stray <br> inside an
// emptied-out contentEditable, which it does routinely; that's why the
// placeholder wasn't showing). textContent catches that case; innerHTML
// alone wouldn't (a lone "<br>" has empty textContent but non-empty HTML).
const isBlank = (el) => !el || (el.textContent.trim() === "" && !el.querySelector("img"));

// fieldFormat turns a merge-field name into the text inserted at the caret.
// Email templates keep the ${field} form; offer letters pass {{field}}.
export const RichTextEditor = ({ value, onChange, placeholder, mergeFields = [], minHeight = 140, fieldFormat = (f) => "${" + f + "}" }) => {
  const ref = useRef(null);
  // Starts as a sentinel (not `value`) so the very first effect run always
  // syncs the initial content into the DOM — contentEditable divs render
  // empty by default; nothing else populates them on mount.
  const lastPushedValue = useRef(undefined);
  const [empty, setEmpty] = useState(!value);

  // Only stomp the DOM when `value` changed for a reason other than our
  // own onChange (e.g. the parent swapped to editing a different record).
  useEffect(() => {
    if (ref.current && value !== lastPushedValue.current) {
      ref.current.innerHTML = value || "";
      lastPushedValue.current = value;
      setEmpty(isBlank(ref.current));
    }
  }, [value]);

  const push = useCallback(() => {
    if (!ref.current) return;
    const html = ref.current.innerHTML;
    lastPushedValue.current = html;
    setEmpty(isBlank(ref.current));
    onChange(html);
  }, [onChange]);

  const exec = (cmd, arg) => {
    ref.current?.focus();
    document.execCommand(cmd, false, arg);
    push();
  };

  const insertLink = async () => {
    const url = await promptDialog("Link URL:", { title: "Insert link", defaultValue: "https://", placeholder: "https://example.com" });
    if (url) exec("createLink", url);
  };

  const insertField = (field) => {
    if (!field) return;
    ref.current?.focus();
    document.execCommand("insertText", false, fieldFormat(field));
    push();
  };
  const [pickerField, setPickerField] = useState("");

  return (
    <div className="border border-b0 rounded-[10px] overflow-hidden bg-bg1">
      <div className="flex flex-wrap items-center gap-[2px] px-[6px] py-[4px] bg-bg2 border-b border-b0">
        <ToolBtn icon={Undo2} title="Undo" onClick={() => exec("undo")} />
        <ToolBtn icon={Redo2} title="Redo" onClick={() => exec("redo")} />
        <div className="w-px h-[16px] bg-b1 mx-[3px]" />
        <select
          onMouseDown={(e) => e.stopPropagation()}
          onChange={(e) => { if (e.target.value) exec("formatBlock", e.target.value); e.target.value = ""; }}
          defaultValue=""
          className="text-[14px] bg-transparent border-0 outline-none cursor-pointer text-t2 py-[4px]"
        >
          <option value="" disabled>Style</option>
          {BLOCK_STYLES.map((b) => <option key={b.v} value={b.v}>{b.l}</option>)}
        </select>
        <select
          onMouseDown={(e) => e.stopPropagation()}
          onChange={(e) => { if (e.target.value) exec("fontName", e.target.value); e.target.value = ""; }}
          defaultValue=""
          className="text-[14px] bg-transparent border-0 outline-none cursor-pointer text-t2 py-[4px]"
        >
          <option value="" disabled>Font</option>
          {FONT_FAMILIES.map((f) => <option key={f} value={f} style={{ fontFamily: f }}>{f}</option>)}
        </select>
        <div className="w-px h-[16px] bg-b1 mx-[3px]" />
        <ToolBtn icon={Bold} title="Bold" onClick={() => exec("bold")} />
        <ToolBtn icon={Italic} title="Italic" onClick={() => exec("italic")} />
        <ToolBtn icon={Underline} title="Underline" onClick={() => exec("underline")} />
        <ToolBtn icon={Strikethrough} title="Strikethrough" onClick={() => exec("strikeThrough")} />
        <ToolBtn icon={Subscript} title="Subscript" onClick={() => exec("subscript")} />
        <ToolBtn icon={Superscript} title="Superscript" onClick={() => exec("superscript")} />
        <div className="w-px h-[16px] bg-b1 mx-[3px]" />
        <select
          onMouseDown={(e) => e.stopPropagation()}
          onChange={(e) => { if (e.target.value) exec("fontSize", e.target.value); e.target.value = ""; }}
          defaultValue=""
          className="text-[14px] bg-transparent border-0 outline-none cursor-pointer text-t2 py-[4px]"
        >
          <option value="" disabled>Size</option>
          {FONT_SIZES.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
        </select>
        <SwatchPicker icon={Palette} title="Text color" colors={COLORS} onPick={(c) => exec("foreColor", c)} />
        <SwatchPicker icon={Highlighter} title="Highlight" colors={HIGHLIGHTS} onPick={(c) => exec("hiliteColor", c)} />
        <div className="w-px h-[16px] bg-b1 mx-[3px]" />
        <ToolBtn icon={List} title="Bullet list" onClick={() => exec("insertUnorderedList")} />
        <ToolBtn icon={ListOrdered} title="Numbered list" onClick={() => exec("insertOrderedList")} />
        <ToolBtn icon={Outdent} title="Outdent" onClick={() => exec("outdent")} />
        <ToolBtn icon={Indent} title="Indent" onClick={() => exec("indent")} />
        <div className="w-px h-[16px] bg-b1 mx-[3px]" />
        <ToolBtn icon={AlignLeft} title="Align left" onClick={() => exec("justifyLeft")} />
        <ToolBtn icon={AlignCenter} title="Align centre" onClick={() => exec("justifyCenter")} />
        <ToolBtn icon={AlignRight} title="Align right" onClick={() => exec("justifyRight")} />
        <ToolBtn icon={AlignJustify} title="Justify" onClick={() => exec("justifyFull")} />
        <div className="w-px h-[16px] bg-b1 mx-[3px]" />
        <ToolBtn icon={Link2} title="Insert link" onClick={insertLink} />
        <ToolBtn icon={Eraser} title="Clear formatting" onClick={() => exec("removeFormat")} />
      </div>

      {mergeFields.length > 0 && (
        <div className="flex flex-wrap items-center gap-[6px] px-[8px] py-[6px] bg-bg2 border-b border-b0">
          <span className="text-[12px] text-t3 font-semibold whitespace-nowrap">Available merge fields</span>
          <select
            value={pickerField}
            onChange={(e) => setPickerField(e.target.value)}
            onMouseDown={(e) => e.stopPropagation()}
            className="text-[14px] bg-bg1 border border-b0 rounded-[6px] px-[6px] py-[3px] outline-none text-t1"
          >
            <option value="">Select Field</option>
            {mergeFields.map((f) => <option key={f} value={f}>{fieldFormat(f)}</option>)}
          </select>
          <span className="text-[12px] text-t3 font-mono min-w-[60px]">{pickerField ? fieldFormat(pickerField) : ""}</span>
          <button
            type="button"
            disabled={!pickerField}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => insertField(pickerField)}
            className="px-[9px] py-[3px] rounded-[6px] text-[12px] font-semibold text-white border-0 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ background: K.p }}
          >
            Insert
          </button>
        </div>
      )}

      <div className="relative bg-bg1">
        {empty && placeholder && (
          <div className="absolute top-[8px] left-[10px] text-[14px] pointer-events-none select-none" style={{ color: K.t3 }}>
            {placeholder}
          </div>
        )}
        <div
          ref={ref}
          contentEditable
          suppressContentEditableWarning
          onInput={push}
          onBlur={push}
          className="px-[10px] py-[8px] text-[14px] text-t0 outline-none bg-bg1 rt-editor"
          style={{ minHeight }}
        />
      </div>
      <style>{`
        .rt-editor ul { list-style: disc; padding-left: 20px; }
        .rt-editor ol { list-style: decimal; padding-left: 20px; }
        .rt-editor a { color: ${K.p}; text-decoration: underline; }
      `}</style>
    </div>
  );
};
